import React from 'react';

/**
 * Card Component
 * 
 * A versatile container component with BankHub styling.
 * Supports multiple variants, hover effects, and clickable cards.
 * 
 * @example
 * ```tsx
 * <Card>
 *   <Card.Header>
 *     <Card.Title>Dashboard</Card.Title>
 *   </Card.Header>
 *   <Card.Content>Content here</Card.Content>
 * </Card>
 * ```
 */

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant */
  variant?: 'default' | 'gradient' | 'glass' | 'flat';
  /** Add hover lift effect */
  hoverable?: boolean;
  /** Make card clickable */
  clickable?: boolean;
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  /** Children content */
  children?: React.ReactNode;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      hoverable = false,
      clickable = false,
      padding = 'md',
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    // Base styles
    const baseStyles = [
      'rounded-2xl',
      'transition-all duration-300',
    ].join(' ');

    // Variant styles
    const variantStyles = {
      default: [
        'bg-white border border-gray-200',
        'shadow-elev-2',
        'dark:bg-dark-card dark:border-dark-border',
      ].join(' '),
      
      gradient: [
        'bg-gradient-to-br from-primary-500 to-primary-700',
        'text-white shadow-elev-3',
        'border-none',
      ].join(' '),
      
      glass: [
        'bg-white/10 backdrop-blur-xl',
        'border border-white/20',
        'shadow-elev-4',
        'dark:bg-dark-card/40',
      ].join(' '),
      
      flat: [
        'bg-gray-50',
        'border-none',
        'shadow-elev-1',
        'dark:bg-dark-surface',
      ].join(' '),
    };

    // Hover styles with elevation upgrade
    const hoverStyles = hoverable
      ? 'hover:shadow-elev-3 hover:-translate-y-1 hover:scale-[1.01]'
      : '';

    // Clickable styles with micro-interaction
    const clickableStyles = clickable
      ? 'cursor-pointer active:scale-[0.98] transition-transform'
      : '';

    // Padding styles
    const paddingStyles = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
      xl: 'p-10',
    };

    // Combine all styles
    const combinedStyles = [
      baseStyles,
      variantStyles[variant],
      hoverStyles,
      clickableStyles,
      paddingStyles[padding],
      className,
    ].join(' ');

    return (
      <div ref={ref} className={combinedStyles} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header
 */
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`mb-4 ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * Card Title
 */
export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children?: React.ReactNode;
  /** Title size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ size = 'lg', className = '', children, ...props }, ref) => {
    const sizeStyles = {
      sm: 'text-lg',
      md: 'text-xl',
      lg: 'text-2xl',
      xl: 'text-3xl',
    };

    return (
      <h3
        ref={ref}
        className={`font-bold text-gray-900 dark:text-dark-text ${sizeStyles[size]} ${className}`}
        {...props}
      >
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

/**
 * Card Description
 */
export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children?: React.ReactNode;
}

const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={`text-sm text-gray-600 dark:text-gray-400 mt-1 ${className}`}
        {...props}
      >
        {children}
      </p>
    );
  }
);

CardDescription.displayName = 'CardDescription';

/**
 * Card Content
 */
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      <div ref={ref} className={className} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

/**
 * Card Footer
 */
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`mt-6 pt-4 border-t border-gray-200 dark:border-dark-border ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

// Compound component pattern
export default Object.assign(Card, {
  Header: CardHeader,
  Title: CardTitle,
  Description: CardDescription,
  Content: CardContent,
  Footer: CardFooter,
});
